const BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '/v1'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly detail?: string,
    readonly title?: string,
  ) {
    super(message)
  }
}

type ProblemBody = {
  title?: string
  detail?: string
  code?: string
  status?: number
}

function redirectToLogin() {
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/connexion') return
  const redirect = `${window.location.pathname}${window.location.search}`
  const q = redirect && redirect !== '/' ? `?redirect=${encodeURIComponent(redirect)}` : ''
  window.location.href = `/connexion${q}`
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ProblemBody | null
    const title = body?.title
    const detail = body?.detail
    const code = body?.code
    if (res.status === 401) {
      redirectToLogin()
    }
    throw new ApiError(
      res.status,
      detail ?? title ?? `La requête ${path} a échoué (${res.status}).`,
      code,
      detail,
      title,
    )
  }
  if (res.status === 204) return undefined as T
  const text = await res.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

export function apiMutate<T>(
  path: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  body?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  return api<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
    headers,
  })
}

/** Download a staff-authenticated CSV export. */
export async function downloadCsv(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { accept: 'text/csv' },
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ProblemBody | null
    if (res.status === 401) redirectToLogin()
    throw new ApiError(
      res.status,
      body?.detail ?? body?.title ?? `Export impossible (${res.status}).`,
      body?.code,
      body?.detail,
      body?.title,
    )
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export interface StaffMe {
  id: string
  email: string
  fullName: string
  role: string
  permissions: string[]
  mustChangePassword: boolean
  expiresAt: string
  idleTimeoutSec: number
}

export interface Category {
  id?: string
  slug: string
  name: string
  tagline: string | null
  productCount?: number
}

export interface StockInfo {
  available: number
  threshold?: number
  onHand?: number
  reserved?: number
  label: string
  tone: string
}

export interface ProductRow {
  id: string
  sku: string
  slug: string
  name: string
  status?: string
  category: { id?: string; slug: string; name: string }
  priceCents: number
  stock: StockInfo
  image: { src: string; lqip: string; alt: string } | null
}

export interface ProductDetail extends ProductRow {
  description: string
  colors: string[]
  powered: string | null
  comparePriceCents: number | null
  weightGrams: number | null
  packaging: string | null
  supplier: string | null
  leadTimeDays: number | null
  variants: Array<{
    id: string
    sku: string
    color: string
    label?: string
    stock: number
    onHand?: number
    reserved?: number
    available?: number
    threshold?: number | null
    priceCents: number | null
    isDefault?: boolean
    active?: boolean
  }>
  images: Array<{ id: string; src: string; alt: string; position: number }>
  inventory: { onHand: number; reserved: number; threshold: number } | null
}

export interface InventoryVariantRow {
  variantId: string
  sku: string
  label: string
  color: string
  active: boolean
  isDefault: boolean
  onHand: number
  reserved: number
  available: number
  threshold: number
  tone: string
  labelStatus?: string
  incoming: number
}

export interface InventoryRow {
  productId: string
  sku: string
  name: string
  category: string
  image: { src: string; alt: string } | null
  onHand: number
  reserved: number
  available: number
  threshold: number
  tone: string
  label: string
  priceCents: number
  variants?: InventoryVariantRow[]
}

export interface OrderRow {
  reference: string
  status: string
  totalCents: number
  contactName: string
  contactPhone?: string
  mode?: string
  placedAt: string
  itemCount?: number
}

export interface BadgeCounts {
  lowStock: number
  pendingReviews: number
  ordersToPrepare: number
}
